/* eslint-disable max-len */
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import {
  Icon, Loader, Menu,
} from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/LibraryResourceCard.less';

import lockedCardIcon from '../img/icon-locked-card.svg';
import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import ImageService from '../services/ImageService';
import PopupService from '../services/PopupService';

import { makeAutoFocusable } from '../utils/focus';
import { makeKeyboardAccessible } from '../utils/makeKeyboardAccessible';

@inject('dialogManager', 'navigationManager', 'schoolManager', 'userManager', 'libraryManager')
@observer
class LibraryResourceCard extends Component {
  constructor(props) {
    super(props);
    this.SCCheckbox = SatCoreComponent('SCCheckbox');
    this.InfoIcon = SatCoreComponent('InfoIcon');
    this.kebabPopupMountNode = React.createRef();
    this.state = {
      resourceId: '',
      resourceTitle: '',
      resourceType: ''
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { libraryCardParams, libraryManager, showLiveBanner, horizontalLayout } = this.props;
    const { state } = this;
    const { shouldAlwaysOpenKebabOnProductLineLibraryCardClick } = libraryManager;
    if (!prevState.popupOpen && state.popupOpen) {
      const entityTypeId = libraryCardParams?.libraryResource?.entityTypeId;
      const isInteractiveKebab = !showLiveBanner || entityTypeId === 'assessment';
      // Product-line cards that open the kebab from the card body, plus alternate-style
      // click kebabs, should move focus into the menu for keyboard/screen reader users.
      if (shouldAlwaysOpenKebabOnProductLineLibraryCardClick || (!horizontalLayout && isInteractiveKebab)) {
        makeAutoFocusable('.resource-option-popup.visible', this.handleClosePopup);
      }
    }
  }

  handleOpenPopup = () => {
    this.setState({ popupOpen: true });
  }

  handleClosePopup = () => {
    this.setState({ popupOpen: false });
  }

  getKebabReturnFocusTarget = () => {
    const mountNode = this.kebabPopupMountNode?.current;
    if (!mountNode) {
      return null;
    }
    return mountNode.querySelector('.library-card-kebab') || mountNode.closest('[tabindex="0"]');
  }

  handleKebabActivate = (event) => {
    if (event) {
      event.stopPropagation();
    }
    if (this.state.popupOpen) {
      this.handleClosePopup();
    } else {
      this.handleOpenPopup();
    }
  }

  renderMenuContent = () => {
    const { libraryCardParams, libraryCardKebabOptions } = this.props;
    const { popupOpen } = this.state;
    const kebabOptions = [];
    if (libraryCardKebabOptions) {
      for (const kebabOptionKey of Object.keys(libraryCardKebabOptions)) {
        const kebabOption = libraryCardKebabOptions[kebabOptionKey];
        if (kebabOption.disabled) {
          kebabOptions.push(
            <React.Fragment key={`option-${kebabOption.key}-${libraryCardParams.id}`}>
              {PopupService.renderPopup({
                className: 'resource-option-popup',
                content: <div className='library-card-title-text disabled'>{kebabOption.disabledMessage}</div>,
                on: 'hover',
                position: 'bottom left',
                trigger: (
                  <Menu.Item key={`${kebabOption.key}-${libraryCardParams.id}`}
                    aria-disabled='true'
                    className={`resource-option-text ${kebabOption.disabled ? 'disabled' : ''}`}
                    name={kebabOption.label}
                    role='menuitem'
                    tabIndex={0}>
                    {kebabOption.label}
                  </Menu.Item>
                )
              })}
            </React.Fragment>
          );
        } else {
          const menuItemClickHandler = () => {
            // Focus the kebab before the menu unmounts. A modal opened from this
            // option can return focus here when it closes.
            this.getKebabReturnFocusTarget()?.focus();
            if (popupOpen) {
              this.handleClosePopup();
            }
            kebabOption.callback(libraryCardParams);
          };
          kebabOptions.push(
            <Menu.Item key={`${kebabOption.key}-${libraryCardParams.id}`}
              name={kebabOption.label}
              {...makeKeyboardAccessible(menuItemClickHandler, { role: 'menuitem' })}>
              {kebabOption.label}
            </Menu.Item>
          );
        }
      }
      return (
        <Menu vertical>
          {kebabOptions}
        </Menu>
      );
    } else {
      return (
        <Menu vertical>
          <Loader key={0} active />
        </Menu>
      );
    }
  }

  getImageAltText = ({ isCustomImage, libraryResource, resourceName }) => (
    ImageService.getContentImageAltText({
      imageAltText: isCustomImage ? libraryResource?.altText : '',
      courseElementName: resourceName
    })
  )

  renderTeacherAssignedBannerIcon() {
    const { t, userManager } = this.props;
    const { InfoIcon } = this;
    return (
      <>

      </>
    );
  }

  renderAlternateCardStyle() {
    const {
      isShopping, isSelected, isSelectDisabled, onBodyClick,
      libraryCardParams, selectHandler, showClassroomsBanner,
      showLiveBanner, t, userManager
    } = this.props;
    const { SCCheckbox, state } = this;
    const { popupOpen } = state;
    // libraryResource is the object for this entity so either a course, workspace (item bank)
    const { backgroundImg, cardClassName, libraryResource, title, typeDisplayName } = libraryCardParams;
    const { entityTypeId, inApproval, published, friendlyName } = libraryResource;
    // we can fit about 168 chars of the title on the card face for a normal card, less for resourceBank.
    const maxTitleLength = 35;
    const finalTitle = friendlyName || title;
    const truncateTitle = finalTitle.length > maxTitleLength;
    const truncatedTitle = truncateTitle ? `${finalTitle.substring(0, maxTitleLength - 3)}...` : finalTitle;
    const isCustomImage = cardClassName && cardClassName.includes('customImg');
    const imageAltText = this.getImageAltText({ isCustomImage, libraryResource, resourceName: finalTitle });
    const backgroundImageStyle = {
      backgroundImage: `url(${backgroundImg})`,
      backgroundSize: 'cover',
    };
    const assignedBannerLabel = userManager.isTeacher ? t('isLiveLabelAssessmentTeacher') : t('isLiveLabelAssessment');
    const updatingToLiveLabel = userManager.isTeacher ? t('updatingToLiveTeacherLabel') : t('updatingToLiveLabel');
    const updatingToLive = showLiveBanner && inApproval && !published;
    const assigned = showLiveBanner && published && !inApproval;
    const isPrintAssessment = !updatingToLive && !assigned && libraryCardParams.libraryResource.printable;
    const statusParts = [];
    if (updatingToLive) {
      statusParts.push(updatingToLiveLabel);
    }
    if (assigned) {
      statusParts.push(entityTypeId === 'assessment' ? assignedBannerLabel : t('isLiveLabel'));
    }
    if (isPrintAssessment) {
      statusParts.push(t('printOnlyLabel'));
    }
    if (libraryResource.adminOnly) {
      statusParts.push(t('adminOnlyLabel'));
    }
    if (showClassroomsBanner) {
      statusParts.push(t('classroomsBannerLabel'));
    }
    const statusLabel = statusParts.length ? `, ${statusParts.join(', ')}` : '';
    const cardAriaLabel = t('alternateCardAriaLabel', {
      status: statusLabel,
      title: finalTitle,
      type: typeDisplayName
    });
    const kebabAriaLabel = t('kebabAriaLabel', { title: finalTitle });
    const kebabDisabledAriaLabel = `${typeDisplayName} ${t('updatingToLiveMessageSfx')}`;
    const titleTextClassName = `library-card-title-text resourceBank ${showLiveBanner ? 'withBanner' : ''}`;
    return (
      <div className='library-card-wrapper'>
        {isShopping && (
          <div className='resource-selection-card-checkbox'>
            <SCCheckbox
              checked={isSelected}
              disabled={isSelectDisabled}
              onChange={(event) => selectHandler(event, libraryCardParams)} />
          </div>
        )}
        <div className={classNames('library-card', cardClassName, {
          disabled: (showLiveBanner && inApproval)
        })}>
          <div
            className={classNames('library-card-body', { clickable: !!onBodyClick })}
            {...(onBodyClick ? makeKeyboardAccessible(
              () => onBodyClick(libraryCardParams),
              { ariaLabel: cardAriaLabel }
            ) : {})}>
            <div aria-hidden={onBodyClick ? 'true' : undefined} className='library-card-title resourceBank'>
              {(truncateTitle) ?
                PopupService.renderPopup({
                  className: 'resource-option-popup',
                  content: <div className={titleTextClassName}>{finalTitle}</div>,
                  on: 'hover',
                  position: 'bottom left',
                  trigger: <div aria-label={finalTitle} className={titleTextClassName} title={finalTitle}>{truncatedTitle}</div>
                })
                :
                <div className={titleTextClassName}>{finalTitle}</div>}
            </div>
            <div aria-hidden={onBodyClick ? 'true' : undefined} className='library-card-image alternate'>
              {isCustomImage ?
                <div aria-label={imageAltText} className='library-card-image-div' role='img' style={backgroundImageStyle} />
                :
                <img alt={imageAltText} src={backgroundImg} />}
              {(updatingToLive) &&
                <div className='live-banner'>{updatingToLiveLabel}</div>}
              {(assigned) &&
                <div className='live-banner'>{entityTypeId === 'assessment' ? assignedBannerLabel : t('isLiveLabel')} {entityTypeId === 'assessment' ? this.renderTeacherAssignedBannerIcon() : ''}</div>}
              {(isPrintAssessment) &&
                <div className='live-banner'>{t('printOnlyLabel')}</div>}
              {(libraryResource.adminOnly) &&
                <div className='live-banner'>{t('adminOnlyLabel')}</div>}
              {(showClassroomsBanner && (
                <div className='live-banner live-banner-classrooms'>{t('classroomsBannerLabel')}</div>
              ))}
            </div>
          </div>
          <div className='library-card-footer' ref={this.kebabPopupMountNode}>
            <div className='resource-type-text left'>{typeDisplayName}</div>
            {(!showLiveBanner || (showLiveBanner && entityTypeId === 'assessment')) &&
              PopupService.renderPopup({
                className: 'resource-option-popup',
                content: this.renderMenuContent(),
                mountNode: this.kebabPopupMountNode.current,
                on: 'click',
                open: popupOpen,
                onClose: this.handleClosePopup,
                onOpen: this.handleOpenPopup,
                position: 'bottom left',
                trigger: (
                  <div
                    className='library-card-kebab'
                    {...makeKeyboardAccessible(this.handleKebabActivate, { ariaLabel: kebabAriaLabel })}
                    aria-expanded={!!popupOpen}
                    aria-haspopup='menu'>
                    <Icon aria-hidden='true' name='ellipsis horizontal' />
                  </div>
                )
              })}
            {showLiveBanner && !(entityTypeId === 'assessment') &&
              PopupService.renderPopup({
                className: 'resource-option-popup',
                content: kebabDisabledAriaLabel,
                on: 'hover',
                open: popupOpen,
                onClose: this.handleClosePopup,
                onOpen: this.handleOpenPopup,
                position: 'bottom left',
                trigger: (
                  <div
                    aria-disabled='true'
                    className='library-card-kebab disabled'
                    {...makeKeyboardAccessible(() => {}, {
                      ariaLabel: kebabDisabledAriaLabel,
                      disabled: true
                    })}>
                    <Icon aria-hidden='true' className='disabled' name='ellipsis horizontal' />
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    );
  }

  renderProductLineCardStyle() {
    const {
      isShopping, isSelected, isSelectDisabled, libraryManager, onBodyClick,
      libraryCardParams, selectHandler, showClassroomsBanner,
      showLiveBanner, t, userManager
    } = this.props;
    const { shouldAlwaysOpenKebabOnProductLineLibraryCardClick } = libraryManager;
    const { SCCheckbox, state } = this;
    const { popupOpen } = state;
    // libraryResource is the object for this entity so either a course, workspace (item bank)
    const { backgroundImg, cardClassName, libraryResource, title, typeDisplayName } = libraryCardParams;
    const { entityTypeId, inApproval, published, friendlyName } = libraryResource;
    // we can fit about 168 chars of the title on the card face for a normal card, less for resourceBank.
    const maxTitleLength = 25;
    const finalTitle = friendlyName || title;
    const truncateTitle = finalTitle.length > maxTitleLength;
    const truncatedTitle = truncateTitle ? `${finalTitle.substring(0, maxTitleLength - 3)}...` : finalTitle;
    
    // Truncate product category similar to title
    const maxProductCategoryLength = 20;
    const finalProductCategory = libraryResource.productCategory || '';
    const truncateProductCategory = finalProductCategory.length > maxProductCategoryLength;
    const truncatedProductCategory = truncateProductCategory ? `${finalProductCategory.substring(0, maxProductCategoryLength - 3)}...` : finalProductCategory;
    const isCustomImage = cardClassName && cardClassName.includes('customImg');
    const imageAltText = this.getImageAltText({ isCustomImage, libraryResource, resourceName: finalTitle });
    const backgroundImageStyle = {
      backgroundImage: `url(${backgroundImg})`,
      backgroundSize: 'cover',
    };
    const assignedBannerLabel = userManager.isTeacher ? t('isLiveLabelAssessmentTeacher') : t('isLiveLabelAssessment');
    const updatingToLiveLabel = userManager.isTeacher ? t('updatingToLiveTeacherLabel') : t('updatingToLiveLabel');
    const updatingToLive = showLiveBanner && inApproval && !published;
    const assigned = showLiveBanner && published && !inApproval;
    const isPrintAssessment = !updatingToLive && !assigned && libraryCardParams.libraryResource.printable;
    const available = libraryResource && libraryResource.available === true;
    const licensed = libraryResource && libraryResource.isLicensedCourse === true;
    const isLocked = !available || !licensed;

    return (
      <div className='library-card-wrapper'>
        {isShopping && (
          <div className='resource-selection-card-checkbox'>
            <SCCheckbox
              checked={isSelected}
              disabled={isSelectDisabled}
              onChange={(event) => selectHandler(event, libraryCardParams)} />
          </div>
        )}
        <div
          className={classNames('library-card product-line', cardClassName, {
            disabled: (showLiveBanner && inApproval)
          })}
          {...(shouldAlwaysOpenKebabOnProductLineLibraryCardClick ? {
            ...makeKeyboardAccessible(
              isLocked ? () => {} : this.handleOpenPopup, {
                ariaLabel: isLocked ? (
                  t('lockedProductLineLibraryCardAriaLabel', { title: finalTitle })
                ) : t('productLineLibraryCardKebabAriaLabel', { title: finalTitle })
              }
            ),
            ...(isLocked && { 'aria-disabled': 'true' })
          } : {})}>
          <div className={`library-card-image product-line${isLocked ? ' is-locked' : ''} ${onBodyClick && !isLocked ? 'clickable' : ''}`} onClick={onBodyClick && !isLocked ? () => onBodyClick(libraryCardParams) : null}>
            {isCustomImage ?
              <div aria-label={imageAltText} className='library-card-image-div' role='img' style={backgroundImageStyle} />
              :
              <img alt={imageAltText} src={backgroundImg} />}
            {isLocked && (
              <img
                alt='Locked'
                className='locked-card-icon'
                src={lockedCardIcon}
              />
            )}
            {(updatingToLive) &&
              <div className='live-banner'>{updatingToLiveLabel}</div>}
            {(assigned) &&
              <div className='live-banner'>{entityTypeId === 'assessment' ? assignedBannerLabel : t('isLiveLabel')} {entityTypeId === 'assessment' ? this.renderTeacherAssignedBannerIcon() : ''}</div>}
            {(isPrintAssessment) &&
              <div className='live-banner'>{t('printOnlyLabel')}</div>}
            {(libraryResource.adminOnly) &&
              <div className='live-banner'>{t('adminOnlyLabel')}</div>}
            {(showClassroomsBanner && (
              <div className='live-banner live-banner-classrooms'>{t('classroomsBannerLabel')}</div>
            ))}
          </div>
          <div className='library-card-footer' ref={this.kebabPopupMountNode}>
            <div className='resource-type-text left' title={finalProductCategory}>{truncatedProductCategory}</div>
            {(!isLocked && (!showLiveBanner || (showLiveBanner && entityTypeId === 'assessment'))) &&
              PopupService.renderPopup({
                className: 'resource-option-popup',
                content: this.renderMenuContent(),
                mountNode: this.kebabPopupMountNode.current,
                on: 'click',
                open: popupOpen,
                onClose: this.handleClosePopup,
                onOpen: this.handleOpenPopup,
                position: 'bottom left',
                trigger: <Icon name='ellipsis horizontal' />
              })}
            {(!isLocked && showLiveBanner && !(entityTypeId === 'assessment')) &&
              PopupService.renderPopup({
                className: 'resource-option-popup',
                content: `${typeDisplayName} ${t('updatingToLiveMessageSfx')}`,
                on: 'hover',
                open: popupOpen,
                onClose: this.handleClosePopup,
                onOpen: this.handleOpenPopup,
                position: 'bottom left',
                trigger: <Icon className='disabled' name='ellipsis horizontal' />
              })}
          </div>
          <div className='library-card-footer'>
            <div className='resource-type-text' title={finalTitle}>{truncatedTitle}</div>
          </div>
        </div>
      </div>
    );
  }

  renderCartModalCard() {
    const {
      isShopping, isSelected, isSelectDisabled, onBodyClick,
      libraryCardParams, selectHandler, showClassroomsBanner,
      showLiveBanner, t
    } = this.props;
    const { SCCheckbox } = this;
    // libraryResource is the object for this entity so either a course, workspace (item bank)
    const { backgroundImg, cardClassName, libraryResource, title, typeDisplayName } = libraryCardParams;
    const { entityTypeId, inApproval, published, friendlyName } = libraryResource;
    // we can fit about 125 chars of the title on the horizontal card.
    const maxTitleLength = 130;
    const finalTitle = friendlyName || title;
    const truncateTitle = finalTitle.length > maxTitleLength;
    const truncatedTitle = truncateTitle ? `${finalTitle.substring(0, maxTitleLength - 3)}...` : finalTitle;
    const isCustomImage = cardClassName && cardClassName.includes('customImg');
    const imageAltText = this.getImageAltText({ isCustomImage, libraryResource, resourceName: finalTitle });
    return (
      <div className='library-card-wrapper'>
        {isShopping && (
          <div className='resource-selection-card-checkbox modal-layout'>
            <SCCheckbox
              checked={isSelected}
              customClassName='modal-layout'
              disabled={isSelectDisabled}
              onChange={(event) => selectHandler(event, libraryCardParams)} />
          </div>
        )}
        <div className={classNames('library-card', cardClassName, 'horizontal', {
          disabled: (showLiveBanner && inApproval)
        })}>
          <div className={`library-card-image ${onBodyClick ? 'clickable' : ''}`} onClick={onBodyClick ? () => onBodyClick(libraryCardParams) : null}>
            <img alt={imageAltText} src={backgroundImg} />
            {(showLiveBanner && inApproval && !published) &&
              <div className='live-banner'>{t('updatingToLiveLabel')}</div>}
            {(showLiveBanner && published && !inApproval) &&
              <div className='live-banner'>{entityTypeId === 'assessment' ? assignedBannerLabel : t('isLiveLabel')}</div>}
            {(showClassroomsBanner && (
              <div className='live-banner live-banner-classrooms'>{t('classroomsBannerLabel')}</div>
            ))}
          </div>
          <div className={`library-card-title ${onBodyClick ? 'clickable' : ''}`} onClick={onBodyClick ? () => onBodyClick(libraryCardParams) : null}>
            {(truncateTitle) ?
              PopupService.renderPopup({
                className: 'resource-option-popup',
                content: <div className={`library-card-title-text resourceBank ${showLiveBanner ? 'withBanner' : ''}`}>{finalTitle}</div>,
                on: 'hover',
                position: 'bottom left',
                trigger: <div className={`library-card-title-text resourceBank ${showLiveBanner ? 'withBanner' : ''}`}>{truncatedTitle}</div>
              })
              :
              <div className={`library-card-title-text resourceBank ${showLiveBanner ? 'withBanner' : ''} ${onBodyClick ? 'clickable' : ''}`} onClick={onBodyClick ? () => onBodyClick(libraryCardParams) : null}>{finalTitle}</div>}
            <div className='resource-type-text horizontal'>{typeDisplayName}</div>
          </div>
        </div>
      </div>
    );
  }

  renderDefaultCard() {
    const {
      isShopping, isSelected, isSelectDisabled, onBodyClick,
      libraryCardParams, selectHandler, showClassroomsBanner,
      showLiveBanner, t, userManager
    } = this.props;
    const { SCCheckbox, state } = this;
    const { popupOpen } = state;
    // libraryResource is the object for this entity so either a course, workspace (item bank)
    const { backgroundImg, cardClassName, libraryResource, title, typeDisplayName } = libraryCardParams;
    const { /* contextType, */entityTypeId, inApproval, published, friendlyName } = libraryResource;
    // we can fit about 168 chars of the title on the card face.
    const finalTitle = friendlyName || title;
    const truncateTitle = finalTitle.length > 168;
    const truncatedTitle = truncateTitle ? `${finalTitle.substring(0, 145)}...` : finalTitle;
    const assignedBannerLabel = userManager.isTeacher ? t('isLiveLabelAssessmentTeacher') : t('isLiveLabelAssessment');
    const updatingToLiveLabel = userManager.isTeacher ? t('updatingToLiveTeacherLabel') : t('updatingToLiveLabel');
    const updatingToLive = showLiveBanner && inApproval && !published;
    const assigned = showLiveBanner && published && !inApproval;
    const isPrintAssessment = !updatingToLive && !assigned && libraryCardParams.libraryResource.printable;
    return (
      <div className='library-card-wrapper'>
        {isShopping && (
          <div className='resource-selection-card-checkbox'>
            <SCCheckbox
              checked={isSelected}
              disabled={isSelectDisabled}
              onChange={(event) => selectHandler(event, libraryCardParams)} />
          </div>
        )}
        <div className={classNames('library-card', cardClassName, {
          disabled: (showLiveBanner && inApproval)
        })} style={{ backgroundImage: `url(${backgroundImg})` }}>
          <div className={`library-card-title ${onBodyClick ? 'clickable' : ''}`} onClick={onBodyClick ? () => onBodyClick(libraryCardParams) : null}>
            {(truncateTitle) ?
              PopupService.renderPopup({
                className: 'resource-option-popup',
                content: <div className={`library-card-title-text ${showLiveBanner ? 'withBanner' : ''}`}>{finalTitle}</div>,
                on: 'hover',
                position: 'bottom left',
                trigger: <div className={`library-card-title-text ${showLiveBanner ? 'withBanner' : ''}`}>{truncatedTitle}</div>
              })
              :
              <div className={`library-card-title-text ${showLiveBanner ? 'withBanner' : ''} ${onBodyClick ? 'clickable' : ''}`}>{finalTitle}</div>}
          </div>
          {(updatingToLive) &&
            <div className='live-banner'>{updatingToLiveLabel}</div>}
          {(assigned) &&
            <div className='live-banner'>{entityTypeId === 'assessment' ? assignedBannerLabel : t('isLiveLabel')}</div>}
          {(isPrintAssessment) &&
            <div className='live-banner'>{t('printOnlyLabel')}</div>}
          {(showClassroomsBanner && (
            <div className='live-banner live-banner-classrooms'>{t('classroomsBannerLabel')}</div>
          ))}
          <div className='library-card-footer'>
            <div className='resource-type-text left'>{typeDisplayName}</div>
            {(!showLiveBanner || (showLiveBanner && entityTypeId === 'assessment')) &&
              PopupService.renderPopup({
                className: 'resource-option-popup',
                content: this.renderMenuContent(),
                on: 'hover',
                open: popupOpen,
                onClose: this.handleClosePopup,
                onOpen: this.handleOpenPopup,
                position: 'bottom left',
                trigger: <Icon name='ellipsis horizontal' />
              })}
            {showLiveBanner && !(entityTypeId === 'assessment') &&
              PopupService.renderPopup({
                className: 'resource-option-popup',
                content: `${typeDisplayName} ${t('updatingToLiveMessageSfx')}`,
                on: 'hover',
                open: popupOpen,
                onClose: this.handleClosePopup,
                onOpen: this.handleOpenPopup,
                position: 'bottom left',
                trigger: <Icon className='disabled' name='ellipsis horizontal' />
              })}

          </div>
        </div>
      </div>
    );
  }

  render() {
    const { horizontalLayout } = this.props;
    const useAlternateCardStyle = !horizontalLayout;
    const { libraryManager } = this.props;
    // Check for ?view=content in the URL
    let urlHasViewContent = false;
    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      urlHasViewContent = params.get('view') === 'content';
    }
    const useProductLineCardStyle = libraryManager.useProductLineLibrary && urlHasViewContent;
    if (useProductLineCardStyle) {
      return this.renderProductLineCardStyle();
    } else if (useAlternateCardStyle) {
      return this.renderAlternateCardStyle();
    } else if (horizontalLayout) {
      return this.renderCartModalCard();
    } else {
      // this is deprecated as we decided to use alternate as default
      return this.renderDefaultCard();
    }
  }
}
export default LibraryResourceCard;

SatCoreRegister('LibraryResourceCard', LibraryResourceCard);
